import { createSelector } from 'reselect';
import { StateAll } from '../../models';
import { AuthState, initialAuthState, LoginState } from './model';
import { getStoreId } from './storage';
import { normalizeLoginModules } from './normalize-login-modules';

const rootSelector = (state: StateAll) => state['common/auth'] || initialAuthState;

const loginSelector = (state: StateAll) => {
  const authState = rootSelector(state);
  return authState.login;
};

export const getLoginResponse = createSelector(loginSelector, (state: LoginState) => state && state.response);

export const getUpdateProfileResponse = createSelector(
  rootSelector,
  (state: AuthState) => state && state.profile && state.profile.response
);

export const isAuthenticated = createSelector(
  getLoginResponse,
  (state) => state && state.success && state.data && state.data.token && state.data.user
);

export const getToken = createSelector(getLoginResponse, (state) => state && state.data && state.data.token);

export const getUser = createSelector(getLoginResponse, (state) => state && state.data && state.data.user);

export const getLoginStoreId = createSelector(
  getLoginResponse,
  (state) => state && state.data && state.data.user && state.data.user.login_store_id
);
export const getLoginWorkstationId = createSelector(
  getLoginResponse,
  (state) => state && state.data && state.data.user && state.data.user.login_workstation_id
);

export const getLoginModules = createSelector(getLoginResponse, (state) => {
  const data = state && state.data;

  if (data && data.user && data.permissions) {
    return normalizeLoginModules(data.permissions.modules);
  }

  return [];
});

export const getPermissions = createSelector(getLoginResponse, (state) => {
  const data = state && state.data;

  return (data && data.user && data.permissions) || {};
});

/**
 * Read cached owner plan profile from localStorage.
 * Storage schema persisted by plan-service:
 *   key: `espa247_owner_plan_profile_cache_v1_${storeId}`
 *   value: { timestamp: number, data: OwnerPlanProfileResponse }
 */
const OWNER_PLAN_STORAGE_KEY = 'espa247_owner_plan_profile_cache_v1';

const fetchPlanProfile = (): any | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const storeId = getStoreId();
    if (!storeId) return null;
    const raw = window.localStorage.getItem(`${OWNER_PLAN_STORAGE_KEY}_${storeId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // parsed.data is OwnerPlanProfileResponse; parsed.data.data is OwnerPlanProfile
    return parsed && parsed.data && parsed.data.data ? parsed.data.data : null;
  } catch (_err) {
    return null;
  }
};

export const getLoginMaximumEmployees = createSelector(getLoginResponse, (state) => {
  // Prefer plan profile cache
  const profile = fetchPlanProfile();
  const maxFromProfile = profile && profile.limits && profile.limits.max_employees;
  if (maxFromProfile !== undefined && maxFromProfile !== null) {
    const numeric = typeof maxFromProfile === 'string' ? Number(maxFromProfile) : maxFromProfile;
    return Number.isFinite(numeric) ? numeric : 0;
  }

  // Fallback: login permissions
  const data = state && state.data;

  if (data && data.user && data.permissions) {
    return data.permissions.maximumEmployees;
    //return getMaximumEmployees(data.owner);
  }

  return 0;
});

/**
 * Plan profile from billing cache (current store).
 * Use in permission-role to sync module list with billing plan (enable/disable by plan).
 */
export const getPlanProfileFromCache = (state: StateAll): any | null => fetchPlanProfile();

export const getCheckPinCodeResponse = createSelector(rootSelector, (state: AuthState) => state && state.checkPinCode);
